/**
 * Copyright (C) Siemens AG 1996-2025 ALL RIGHTS RESERVED.
 */

import {Component, effect, Input, inject} from '@angular/core';

import {FormFieldAttribute} from '../../../../../../../models/FormCategory';
import {IxModule} from '@siemens/ix-angular';
import {TranslateModule} from '@ngx-translate/core';
import {BatteryPassSelection} from '../../../../../../../models/BatteryPassAttributeSelection';
import {BatteryPassAttributeLabel} from '../../../../../../../models/BatteryPassAttributeLabel';
import {AssignmentService} from '../../../../../../../services/assignment.service';
import {PassportService} from '../../../../../../../services/passport.service';
import {UserManagementService} from '../../../../../../../services/user-management.service';

@Component({
  selector: 'app-field-label',
  imports: [IxModule, TranslateModule],
  templateUrl: './field-label.component.html',
  styleUrl: '../input-components.scss',
})
export class FieldLabelComponent {
  readonly assignmentService = inject(AssignmentService);
  readonly passportService = inject(PassportService);
  readonly userManagementService = inject(UserManagementService);

  @Input() attribute!: FormFieldAttribute;
  @Input() passportId!: string;

  isUserListLoading = true;
  attributeAssignments: BatteryPassAttributeLabel[] = [];
  attributeSelections: BatteryPassSelection[] = [];
  showInfo = false;

  constructor() {
    effect(async () => {
      this.attributeSelections =
        this.assignmentService.attributeSelections()[this.passportId];
      this.attributeAssignments =
        this.assignmentService.attributeAssignments()[this.passportId];
    });
    effect(async () => {
      const userList = this.userManagementService.userList();
      if (userList) {
        this.isUserListLoading = false;
      }
    });
  }

  isAttributeDisabled(attribute: string) {
    if (this.attributeAssignments && !this.isUserListLoading) {
      return this.attributeAssignments.some(
        (label) => label.attribute === attribute,
      );
    } else {
      return true;
    }
  }

  onSelectionChange(attributeName: string, event: Event) {
    const checked = (event as never as {target: {checked: boolean}}).target
      .checked;

    // Use the local attributeSelections, default to empty array
    const currentSelections: BatteryPassSelection[] =
      this.attributeSelections ?? [];

    let updatedSelections: BatteryPassSelection[];
    if (checked) {
      // Add or update the attribute
      if (!currentSelections.some((sel) => sel.attribute === attributeName)) {
        updatedSelections = [...currentSelections, {attribute: attributeName}];
      } else {
        updatedSelections = currentSelections.map((sel) =>
          sel.attribute === attributeName ? {...sel, checked} : sel,
        );
      }
    } else {
      // Remove the attribute if unchecked
      updatedSelections = currentSelections.filter(
        (sel) => sel.attribute !== attributeName,
      );
    }

    this.attributeSelections = updatedSelections;
    this.assignmentService.attributeSelections.set({
      ...this.assignmentService.attributeSelections(),
      [this.passportId]: updatedSelections,
    });
  }

  isAttributeSelected(attribute: string) {
    return this.attributeSelections?.some(
      (selection) => selection.attribute === attribute,
    );
  }
}
